import { Injectable, computed, signal } from '@angular/core';
import { MediaPermissionService } from './media-permission.service';
import {
  MediaPermissionState,
  UpdateMediaPermissionRequest,
} from '../../models/consent/media-permission.model';
import { firstValueFrom } from 'rxjs';
import { RecipientPermissionCacheService } from './recipient-permission-cache.service';
import { CurrentUserService } from '../auth/current-user.service';

const DEFAULT_STATE: MediaPermissionState = {
  audioAllowed: false,
  videoAllowed: false,
  kvkkAcknowledged: false,
};

@Injectable({ providedIn: 'root' })
export class MediaPermissionStore {
  private readonly _permissions = signal<MediaPermissionState>(DEFAULT_STATE);
  private readonly storageKey = 'MEDIA_PERMISSION_STATE';

  readonly permissions = this._permissions.asReadonly();
  readonly canAudioCall = computed(() => this._permissions().audioAllowed);
  readonly canVideoCall = computed(
    () => this._permissions().audioAllowed && this._permissions().videoAllowed
  );

  constructor(
    private readonly mediaPermissionService: MediaPermissionService,
    private readonly recipientCache: RecipientPermissionCacheService,
    private readonly currentUserService: CurrentUserService
  ) {
    this.hydrateFromStorage();
  }

  loadFromLoginResponse(response: any): void {
    if (!response) {
      this._permissions.set(DEFAULT_STATE);
      return;
    }

    const kvkkAcknowledged = !!response.kvkkConfirmationDate;
    const audioAllowed = !!response.micPermissionConfirmationDate;
    const videoAllowed = !!response.camPermissionConfirmationDate;

    const updatedAtCandidate =
      response.micPermissionConfirmationDate ||
      response.camPermissionConfirmationDate ||
      response.kvkkConfirmationDate;

    this._permissions.set({
      audioAllowed,
      videoAllowed,
      kvkkAcknowledged,
      updatedAt: updatedAtCandidate
        ? new Date(updatedAtCandidate).toISOString()
        : undefined,
    });
    this.persist();
  }

  setPermissions(next: MediaPermissionState): void {
    this._permissions.set({ ...DEFAULT_STATE, ...next });
    this.persist();
  }

  async syncFromServer(): Promise<void> {
    try {
      const resp = await firstValueFrom(
        this.mediaPermissionService.getMyPermissions()
      );
      if (resp?.data) {
        this._permissions.set({ ...DEFAULT_STATE, ...resp.data });
        this.persist();
      }
    } catch {
      // keep existing state
    }
  }

  async updatePermissions(
    audioAllowed: boolean,
    videoAllowed: boolean,
    extras?: Pick<
      UpdateMediaPermissionRequest,
      'deviceFingerprint' | 'deviceMetadata'
    >
  ): Promise<void> {
    const payload: UpdateMediaPermissionRequest = {
      audioAllowed,
      videoAllowed,
      deviceFingerprint: extras?.deviceFingerprint,
      deviceMetadata: extras?.deviceMetadata,
    };

    try {
      const resp = await firstValueFrom(
        this.mediaPermissionService.updateMyPermissions(payload)
      );
      if (resp?.data) {
        this._permissions.set({
          ...DEFAULT_STATE,
          ...resp.data,
          kvkkAcknowledged: this._permissions().kvkkAcknowledged,
        });
        this.persist();
        const userId = this.currentUserService.GetSession()?.id;
        if (userId) {
          this.recipientCache.invalidate(userId);
        }
        return;
      }
    } catch {
      // ignore and update locally
    }

    this._permissions.update((current) => ({
      ...current,
      audioAllowed,
      videoAllowed,
    }));
    this.persist();
  }

  private hydrateFromStorage(): void {
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as Partial<MediaPermissionState>;
      if (
        typeof parsed.audioAllowed !== 'boolean' ||
        typeof parsed.videoAllowed !== 'boolean'
      ) {
        return;
      }
      this._permissions.set({ ...DEFAULT_STATE, ...parsed });
    } catch {
      // ignore
    }
  }

  private persist(): void {
    try {
      localStorage.setItem(
        this.storageKey,
        JSON.stringify(this._permissions())
      );
    } catch {
      // ignore
    }
  }
}
